/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Button from '../../../components/button';
import ConfirmationModal from '../../../components/modal/confirmationModal';
import Search from '../../../components/search/search';
import Table from '../../../components/table';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { JobTitleHeader } from '../../../constants/table-headers/jobTitleHeader';
import {
  useBoolean,
  useGetCustomerTitleListWithPagination,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import {
  CustomerTitleImportService,
  CustomerTitleService,
  type Wealthlane_CustomerService_Dtos_CustomerTitle_CustomerTitleDto,
} from '../../../services/wealthlane-customer-services';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import { PageHeader } from '../company-profile/profile.styled';
import { SettingRoleButton } from '../system-roles/systemRoleStyled';
import JobTitleModal from './jobTitleModal';
import { ImportBlock } from '../../../constants/style/common.styled';
import { PlainTextWrapper } from '../system-roles/roles.styled';
import { InputField } from '../../participants/participant.styled';
import Spinners from '../../../components/spinner';
import { handleOpenFile } from '../../../utils/global';
import { isEmpty } from 'lodash';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';

const jobTitleState = {
  data: InitialValuesForm.jobTitleForm,
  id: '',
  modalStatus: 'Add' as ModalStatusType,
  loading: false,
  searchQuery: '',
};

const JobTitleComponent = (): JSX.Element => {
  const [jobTitleInfo, setJobTitleInfo] = useState(jobTitleState);
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo } = useDynamicTable();

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmModalOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useBoolean(false);

  // GET LIST
  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneCustomerApi();
  const {
    data: jobTitlesList,
    isLoading,
    status,
  } = useGetCustomerTitleListWithPagination(handleRequest, tableInfo);
  const data = React.useMemo(() => jobTitlesList?.items || [], [jobTitlesList, isLoading]);

  const [importLoader, setImportLoader] = useState<boolean>(false);
  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'JOB_TITLES_TABLE_LIST',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  // TABLE ACTION
  const tableActions = [
    {
      action: 'View',
      onClick: (id: string) => {
        getJobTitleData(id, 'View');
      },
      id: 'viewJobTitleAction',
    },
    {
      action: 'Edit',
      as: 'Edit',
      onClick: (id: string) => {
        getJobTitleData(id, 'Edit');
      },
      id: 'editJobTitleAction',
    },
    {
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      loading: jobTitleInfo.loading,
      onClick: (id: string) => {
        setJobTitleInfo({
          ...jobTitleInfo,
          id,
        });
        openConfirmModal();
      },
      id: 'deleteJobTitleAction',
    },
  ];

  const getJobTitleData = (id: string, type: ModalStatusType): void => {
    setJobTitleInfo({ ...jobTitleInfo, loading: true });
    openModal();
    handleRequest(CustomerTitleService.getApiCustomerApiAppCustomerTitleCustomerTitle(id))
      .then((data?: Wealthlane_CustomerService_Dtos_CustomerTitle_CustomerTitleDto) => {
        if (data != null) {
          setJobTitleInfo({
            ...jobTitleInfo,
            modalStatus: type,
            id,
            data: {
              name: data.name ?? '',
              description: data.description ?? '',
            },
            loading: false,
          });
        }
      })
      .catch(() => {
        setJobTitleInfo({ ...jobTitleInfo, loading: false });
      });
  };

  const deleteJobTitleData = (): void => {
    setJobTitleInfo({ ...jobTitleInfo, loading: true });
    handleRequest(
      CustomerTitleService.deleteApiCustomerApiAppCustomerTitleCustomerTitle(jobTitleInfo.id)
    )
      .then((data?) => {
        if (data != null) {
          setJobTitleInfo({ ...jobTitleInfo, loading: false });
          toast.success(<ToastComponent label='Success' message='Job Title Deleted.' />);
          void queryClient.invalidateQueries('getCustomerTitleListWithPagination');
        }
      })
      .catch(() => { })
      .finally(() => {
        setJobTitleInfo({ ...jobTitleInfo, loading: false });
        closeConfirmModal();
      });
  };

  const downloadTemplate = (): void => {
    handleRequest(
      CustomerTitleImportService.postApiCustomerApiAppCustomerTitleImportDownloadCustomerTitleImportSample()
    )
      .then((data: any) => {
        if (data != null) {
          handleOpenFile('Job Title Template.xlsx', data.content);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };
  const importExcel = (event: any): void => {
    if (!isEmpty(event.target.files)) {
      const payload = {
        ImportFile: event.target.files[0],
      };
      // setImportLoader(true);
      handleRequest(
        CustomerTitleImportService.postApiCustomerApiAppCustomerTitleImportImportCustomerTitle(
          payload
        )
      )
        .then((data: any) => {
          if (data) {
            toast.success(
              <ToastComponent label='Success' message='Job Title import job submitted.' />
            );
            queryClient.invalidateQueries('getCustomerTitleListWithPagination');
            // setImportLoader(false);
          }
        })
        .catch((error: any) => {
          console.log('error', error);
          // setImportLoader(false);
        });
      // .finally(() => {
      //   setImportLoader(false);
      // });
      event.target.value = null;
    }
  };
  const schema = React.useMemo(
    () => ({
      columns: JobTitleHeader({ actions: schemaData?.columns, tableActions }),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        variant={'primary'}
        title={'Add Job Title'}
        icon={'add_circle_outline'}
        id="addJobTitleButton"
        onClick={() => {
          setJobTitleInfo({
            ...jobTitleInfo,
            data: InitialValuesForm.jobTitleForm,
            modalStatus: 'Add',
            id: '',
          });
          openModal();
        }}
      />
    );
  };

  const TemplateDatas = [
    {
      title: 'Download Template',
      download: downloadTemplate,
      id: 'downloadJobTitleTemplate',
    },
    {
      title: 'Import Bulk XLS',
      import: importExcel,
      id: 'importJobTitleBulkXLS',
    },
  ];

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <>
      <PageHeader data-page="jobTitles" id="pageContainer">
        <Heading variant='h3' title='Job Titles' id="pageTitle" />
        <Heading
          variant='body-s'
          title={`Current list of Wealthlane system access department for`}
          id="pageDescription"
        />
      </PageHeader>
      <div className='grid sm:grid-cols-12 justify-between gap-4 sm:gap-0'>
        {/* <div className='lg:col-span-3 col-span-6'>
          <Search
            placeholder='Search'
            searchValue={jobTitleInfo.searchQuery}
            onSearch={(value: string) => {
              setJobTitleInfo({
                ...jobTitleInfo,
                searchQuery: value,
              });
            }}
            setTableInfo={setTableInfo}
            tableInfo={tableInfo}
          />
        </div> */}
        {/* <SettingRoleButton className='lg:col-start-10 lg:col-span-3 col-span-6'>
          <Button
            as='Create'
            variant={'primary'}
            title={'Add Job Title'}
            icon={'add_circle_outline'}
            onClick={() => {
              setJobTitleInfo({
                ...jobTitleInfo,
                data: InitialValuesForm.jobTitleForm,
                modalStatus: 'Add',
                id: '',
              });
              openModal();
            }}
          />
        </SettingRoleButton> */}
        <AnimationWrapper>
          {isModalOpen && (
            <JobTitleModal
              id="jobTitleModal"
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              initialData={jobTitleInfo}
              handleRequest={handleRequest}
              setModalStatus={(value) => {
                setJobTitleInfo({
                  ...jobTitleInfo,
                  modalStatus: value,
                });
              }}
              loading={jobTitleInfo.loading}
            />
          )}

          {isConfirmModalOpen && (
            <ConfirmationModal
              isShow={isConfirmModalOpen}
              handleClose={() => {
                closeConfirmModal();
              }}
              title='job title'
              handleSubmit={() => {
                deleteJobTitleData();
              }}
              loading={jobTitleInfo.loading}
            />
          )}
        </AnimationWrapper>
      </div>

      <DynamicTable
        id="jobTitlesTable"
        schema={schema}
        data={data}
        totalCounts={jobTitlesList?.totalCount ?? 10}
        loading={status}
        templateDatas={TemplateDatas}
        // filter={filter}
        components={{
          renderRightToolbarButton,
        }}
      />
      {/* <Table
        columns={JobTitleHeader(actions)}
        data={data?.items ?? []}
        loading={status}
        showPagination
        totalCounts={data?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
    </>
  );
};

export default withDynamicTableContext(JobTitleComponent, false, axiosInstance);
